import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseNavigation } from '@fuse/types';
import { select, Store } from '@ngrx/store';
import { CompanySubset } from 'app/api/company.api.service';
import { User } from 'app/api/user.api.service';
import { AuthService } from 'app/auth/auth.service';
import { defaultNavigation, navigationByCompany } from 'app/navigation/navigation';
import { AmplifyService } from 'aws-amplify-angular';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';

import { CompaniesActions } from './main/company/actions';
import { CompaniesSelectors } from './main/company/selectors';
import { selectJournalCountAnomalies } from './main/journal/journal.selectors';
import { PCGRequested } from './main/pcg/pcg.actions';
import { selectPlanCountAnomalies } from './main/plan/plan.selectors';
import { AppState } from './reducers';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;
    currentUser: User;

    // Protected
    protected logger: any;

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Creates an instance of AppComponent.
     * @param {*} document
     * @param {AmplifyService} amplifyService
     * @param {AuthService} auth
     * @param {Store<AppState>} store
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {Platform} _platform
     * @memberof AppComponent
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        protected amplifyService: AmplifyService,
        protected auth: AuthService,
        protected store: Store<AppState>,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected titleService: Title,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _platform: Platform
    ) {
        // Set logger
        this.logger = this.amplifyService.logger('AppComponent');

        // Get default navigation
        this.navigation = defaultNavigation;

        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get current user
        this.auth.onCurrentUserChanged
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter(user => !!user)
            )
            .subscribe(() => {
                this.currentUser = this.auth.currentUser;
                this.store.dispatch(CompaniesActions.companiesRequested());
                this.store.dispatch(new PCGRequested());
            });

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(CompaniesSelectors.selectCurrentCompany)
            )
            .subscribe((current: CompanySubset) => this.setNavigation(current));



        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectJournalCountAnomalies),
                filter(() => !!this._fuseNavigationService.getNavigationItem('journal'))
            )
            .subscribe((anomalies: number) => {
                this.setJournalAnomalies(anomalies);
            }
            );

        this.store
            .pipe(
                takeUntil(this._unsubscribeAll),
                select(selectPlanCountAnomalies),
                filter(() => !!this._fuseNavigationService.getNavigationItem('plan'))
            )
            .subscribe((anomalies: number) => {
                this.setPlanAnomalies(anomalies);
            }
            );


        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => {
                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                } else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                // tslint:disable-next-line: prefer-for-of
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map(route => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                mergeMap(route => route.data)
            )
            .subscribe(event => this.titleService.setTitle(`${event['title']} | Expo Cube`));
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    // Sets the navigation for the selected company
    setNavigation(company: CompanySubset): void {
        const currentNavigation = this._fuseNavigationService.getCurrentNavigation();

        // Remove company specific items
        currentNavigation.forEach((item: FuseNavigation) => {
            if (item.id !== 'general') {
                this._fuseNavigationService.removeNavigationItem(item.id);
            }
        });

        if (company && company !== null) {
            // New navigation tree for the selected company
            const nav = navigationByCompany(company);
            this._fuseNavigationService.addNavigationItem(nav, 'end');
        }

        this.navigation = this._fuseNavigationService.getCurrentNavigation();
    }

    setJournalAnomalies(anomalies: number): void {
        const props =
            (anomalies === 0)
                ? {
                    badge: null
                }
                : {
                    badge: {
                        hidden: false,
                        title: '' + anomalies,
                        bg: '#F44336',
                        fg: '#FFFFFF'
                    }
                };
        this._fuseNavigationService.updateNavigationItem('journal', props);
    }

    // TODO: DRY
    setPlanAnomalies(anomalies: number): void {
        const props =
            (anomalies === 0)
                ? { badge: null }
                : {
                    badge: {
                        title: '' + anomalies,
                        bg: '#F44336',
                        fg: '#FFFFFF'
                    }
                };
        this._fuseNavigationService.updateNavigationItem('plan', props);
    }

}
